import React from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { useTranslation } from "react-i18next";

const BASE_URL = "https://comparemechanic.com";

/**
 * Reusable SEO component that handles:
 * - Title and meta description
 * - Canonical URL
 * - Open Graph tags
 * - Twitter Card tags
 * - hreflang alternate links (EN/FR/ES)
 * - JSON-LD structured data injection
 * - BreadcrumbList schema
 * - noindex for private pages
 */
function SEOHelmet({
  title,
  description,
  path,
  type = "website",
  image,
  noindex = false,
  noindexNofollow = false,
  schemas = [],
  breadcrumbs,
  keywords,
  article,
  alternates,
}) {
  const { i18n } = useTranslation();

  // When `alternates` ({ en, fr, es }) is passed, the article has explicit
  // localized slugs — use those for canonical + hreflang and skip the
  // automatic ?lang= query-param machinery. This fixes "Hreflang to
  // non-canonical" on tri-lingual articles where each language has its
  // own slug (e.g. /article/cout-reparation-freins-mexique-2026 instead
  // of /article/brake-repair-mexico-cost-2026?lang=fr-CA).
  const useExplicitAlternates = alternates && (alternates.en || alternates.fr || alternates.es);

  // Canonical is ALWAYS the clean, query-free URL — never a ?lang= variant.
  // For pages with real localized slugs the current language's slug is
  // canonical; otherwise the bare path is canonical for every language.
  // (Emitting ?lang= canonicals previously produced "Duplicate / Google chose
  // different canonical" in GSC, since Google consolidates parameter variants
  // to the clean URL anyway.)
  const langKey = i18n.language?.startsWith("fr") ? "fr"
                : i18n.language?.startsWith("es") ? "es"
                : "en";
  const stripQuery = (p) => (p ? p.split("?")[0] : p);
  const canonicalPath = useExplicitAlternates
    ? stripQuery(alternates[langKey] || alternates.en || alternates.es || alternates.fr)
    : stripQuery(path);
  const canonicalUrl = canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL;

  // Real localized alternates = query-free slugs that actually differ from the
  // English URL. City/blog/utility pages that only pass ?lang= variants
  // collapse to the English URL here and emit NO hreflang (correct for
  // single-language pages).
  const cleanAlts = useExplicitAlternates
    ? { en: stripQuery(alternates.en), fr: stripQuery(alternates.fr), es: stripQuery(alternates.es) }
    : null;
  const hasRealFr = !!(cleanAlts?.fr && cleanAlts.fr !== cleanAlts.en);
  const hasRealEs = !!(cleanAlts?.es && cleanAlts.es !== cleanAlts.en);
  const ogImage = image || `${BASE_URL}/og-share.png`;
  // Append brand suffix only when there's room — Google truncates titles at
  // ~60 chars, so adding " | CompareMechanic" (18 chars) to a 50-char title
  // pushes it over and the brand gets cut off anyway. Threshold of 42 keeps
  // the full title + suffix under 60. Long titles drop the suffix and rely
  // on the brand mention already present elsewhere on the page.
  const fullTitle = title
    ? title.length <= 42
      ? `${title} | CompareMechanic`
      : title
    : "CompareMechanic - Find & Compare Auto Repair Garages";

  // Build breadcrumb schema from array of { name, path } items
  const breadcrumbSchema = breadcrumbs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((crumb, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: crumb.name,
          item: crumb.path ? `${BASE_URL}${crumb.path}` : undefined,
        })),
      }
    : null;

  // Combine all schemas
  const allSchemas = [...schemas];
  if (breadcrumbSchema) allSchemas.push(breadcrumbSchema);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {noindex && <meta name="robots" content={noindexNofollow ? "noindex, nofollow" : "noindex, follow"} />}

      {/* hreflang — emitted ONLY when a page has real, query-free localized
          slugs that differ from the English URL (e.g. the tri-lingual Mexico
          articles). Single-language pages, and pages that previously relied on
          the ?lang= fallback, emit no hreflang and a clean self-canonical. */}
      {(hasRealFr || hasRealEs) && (
        <>
          {cleanAlts.en && <link rel="alternate" hrefLang="en" href={`${BASE_URL}${cleanAlts.en}`} />}
          {hasRealFr && <link rel="alternate" hrefLang="fr" href={`${BASE_URL}${cleanAlts.fr}`} />}
          {hasRealEs && <link rel="alternate" hrefLang="es" href={`${BASE_URL}${cleanAlts.es}`} />}
          <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${cleanAlts.en || cleanAlts.fr || cleanAlts.es}`} />
        </>
      )}

      {/* Open Graph */}
      <meta property="og:title" content={title || "CompareMechanic"} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="CompareMechanic" />
      <meta property="og:locale" content={i18n.language === "fr-CA" ? "fr_CA" : i18n.language === "es-MX" ? "es_MX" : "en_CA"} />
      <meta property="og:locale:alternate" content="fr_CA" />
      <meta property="og:locale:alternate" content="es_MX" />

      {/* Article-specific OG tags */}
      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.section && <meta property="article:section" content={article.section} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@CompareMechanic" />
      <meta name="twitter:title" content={title || "CompareMechanic"} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

// Pre-built schema generators

SEOHelmet.organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "CompareMechanic",
  url: BASE_URL,
  logo: `${BASE_URL}/logo_crisp_transparent.png`,
  description:
    "CompareMechanic is a Canadian auto repair comparison platform that helps car owners find, compare, and book trusted garages by price, reviews, and services.",
  sameAs: [
    "https://facebook.com/CompareMechanic",
    "https://x.com/CompareMechanic",
    "https://instagram.com/CompareMechanic",
    "https://www.linkedin.com/company/110163339",
    "https://www.youtube.com/@Comparemechanic",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    email: "contact@comparemechanic.com",
    contactType: "customer service",
    availableLanguage: ["English", "French", "Spanish"],
  },
});

SEOHelmet.websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "CompareMechanic",
  url: BASE_URL,
  description:
    "Find and compare auto repair garages near you. Get quotes, read reviews, and book appointments online.",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${BASE_URL}/search-results?postalCode={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

SEOHelmet.faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

// VideoObject schema for embedded YouTube videos. Required fields per
// schema.org spec: name, description, thumbnailUrl, uploadDate, contentUrl.
// Google rich-result video snippets read this to display the video
// directly in SERPs alongside the page title.
SEOHelmet.videoObjectSchema = ({
  videoId,
  name,
  description,
  uploadDate,
  duration,
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name,
  description,
  thumbnailUrl: [
    `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
  ],
  uploadDate,
  ...(duration && { duration }), // ISO 8601 duration, e.g. "PT2M30S"
  contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
  embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}`,
  publisher: {
    "@type": "Organization",
    name: "CompareMechanic",
    logo: {
      "@type": "ImageObject",
      url: `${BASE_URL}/comparemechanic-logo.png`,
    },
  },
});

SEOHelmet.itemListSchema = (items, name) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    url: item.url,
    ...(item.image && { image: item.image }),
  })),
});

SEOHelmet.localBusinessSchema = (garage, services = [], operatingHours = null) => ({
  "@context": "https://schema.org",
  "@type": "AutoRepair",
  name: garage.business_name,
  description: garage.description?.substring(0, 500),
  address: {
    "@type": "PostalAddress",
    streetAddress: garage.address,
    addressLocality: garage.city,
    addressRegion: garage.province,
    postalCode: garage.postal_code,
    addressCountry: garage.country || "CA",
  },
  ...(garage.gps_coordinates && {
    geo: {
      "@type": "GeoCoordinates",
      latitude: garage.gps_coordinates.lat || garage.gps_coordinates.latitude,
      longitude: garage.gps_coordinates.lng || garage.gps_coordinates.longitude,
    },
  }),
  ...(garage.phone && { telephone: garage.phone }),
  ...(garage.email_1 && { email: garage.email_1 }),
  ...(garage.rating && {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: garage.rating,
      reviewCount: garage.review_count || 1,
      bestRating: 5,
      worstRating: 1,
    },
  }),
  ...(services.length > 0 && {
    makesOffer: services.slice(0, 20).map((s) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: s.service?.name || s.name,
      },
    })),
  }),
  ...(garage.city && { areaServed: garage.city }),
  ...(operatingHours && {
    openingHoursSpecification: SEOHelmet._buildOpeningHours(operatingHours),
  }),
});

SEOHelmet._buildOpeningHours = (hours) => {
  if (!hours || typeof hours !== "object") return undefined;
  const parsed = typeof hours === "string" ? JSON.parse(hours) : hours;
  return Object.entries(parsed)
    .filter(([, h]) => h && !h.closed && h.open && h.close)
    .map(([day, h]) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: day.charAt(0).toUpperCase() + day.slice(1),
      opens: h.open,
      closes: h.close,
    }));
};

SEOHelmet.autoRepairListSchema = (garages, listName) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: listName,
  numberOfItems: garages.length,
  itemListElement: garages.map((g, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "AutoRepair",
      name: g.business_name || g.name,
      ...(g.address && {
        address: {
          "@type": "PostalAddress",
          streetAddress: g.address,
          addressLocality: g.city,
          addressRegion: g.province,
          postalCode: g.postalCode || g.postal_code,
          addressCountry: g.country || "CA",
        },
      }),
      ...(g.coordinates?.lat && g.coordinates?.lng && {
        geo: {
          "@type": "GeoCoordinates",
          latitude: g.coordinates.lat,
          longitude: g.coordinates.lng,
        },
      }),
      ...(g.phone && { telephone: g.phone }),
      ...(typeof g.rating === "number" && g.rating > 0 && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: g.rating,
          reviewCount: g.reviewCount || g.review_count || 1,
          bestRating: 5,
          worstRating: 1,
        },
      }),
      ...(g.operatingHours && {
        openingHoursSpecification: SEOHelmet._buildOpeningHours(g.operatingHours),
      }),
      ...(g.slug && {
        url: `https://comparemechanic.com/garage/${g.slug}`,
      }),
    },
  })),
});

export default SEOHelmet;
